import React, { Dispatch, SetStateAction, useState } from "react";

import { IgCheckbox, IgTypography } from "@intelligems/ig-theme";
import {
  AnalyticsViewType,
  ExperienceCategory,
  GenericExperienceFe,
} from "@intelligems/ig-types";
import { FormControlLabel, Grid, ListItemButton } from "@mui/material";
import { styled } from "@mui/material/styles";

const PREFIX = "AnalyticsViewTypeFilter";

const classes = {
  root: `${PREFIX}-root`,
};

const analyticsViewTypesList = [
  {
    key: "All Orders",
    description:
      "Recommended if test products are substitutes for other products in your store or constitute a large portion (>40%) of your store's revenue.",
    shortDescription: "Include orders regardless of their contents",
    value: "sitewide_orders",
  },
  {
    key: "Orders with Target Products",
    description:
      "Recommended if test products are not substitutes with other products in your store and do not make up a large portion (>40%) of your store's revenue.",
    shortDescription:
      "Include only orders that contain at least one of your target products",
    value: "test_orders_only",
  },
  {
    key: "Ask me later",
    description: "",
    value: "none",
  },
];

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.root}`]: {
    borderRadius: "4px",
    alignContent: "center",
    border: `1px solid ${theme.igBorders.core.default}`,
    textAlign: "center",
    "&:hover, &:focus": {
      border: `1px solid ${theme.igBorders.core.hover}`,
    },
  },
}));

interface AnalyticsViewTypeFilterProps {
  currentExperience?: GenericExperienceFe;
  testOrdersOnly: boolean;
  isTestProductsDefaultChange: boolean;
  setIsTestProductsDefaultChange: Dispatch<SetStateAction<boolean>>;
  onBooleanFilterChange: (
    checked: boolean,
    filterKey: "testOrdersOnly" | "isZScoreEnabled"
  ) => void;
}
export const AnalyticsViewTypeFilter = ({
  currentExperience,
  testOrdersOnly,
  isTestProductsDefaultChange,
  setIsTestProductsDefaultChange,
  onBooleanFilterChange,
}: AnalyticsViewTypeFilterProps) => {
  const [selectedViewType, setSelectedViewType] = useState<
    AnalyticsViewType | undefined
  >(testOrdersOnly ? "test_orders_only" : "sitewide_orders");

  const selectedCard = (viewType: string): boolean => {
    return selectedViewType === viewType;
  };

  const onSelect = (value: AnalyticsViewType) => {
    setSelectedViewType(value);
    onBooleanFilterChange(value === "test_orders_only", "testOrdersOnly");
  };

  if (currentExperience) {
    return (
      <StyledGrid container spacing={2} direction={"row"}>
        {analyticsViewTypesList.slice(0, 2).map((viewType) => (
          <Grid item xs={6} key={viewType.key}>
            <ListItemButton
              classes={{ root: classes.root }}
              onClick={() => {
                onSelect(viewType.value as AnalyticsViewType);
              }}
              selected={selectedCard(viewType.value)}
            >
              <Grid
                container
                direction={"column"}
                alignItems={"center"}
                textAlign={"center"}
                sx={{ height: "75px" }}
                wrap={"nowrap"}
              >
                <Grid item>
                  <IgTypography variant="body2" sx={{ fontWeight: 600 }}>
                    {viewType.key}
                  </IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography variant={"body2"}>
                    {viewType.shortDescription}
                  </IgTypography>
                </Grid>
              </Grid>
            </ListItemButton>
          </Grid>
        ))}

        {currentExperience &&
          currentExperience.category === "experiment" &&
          currentExperience.experienceAnalysis.analyticsViewType !==
            selectedViewType &&
          !(
            currentExperience.experienceAnalysis.analyticsViewType === "none" &&
            selectedViewType === "test_orders_only"
          ) && (
            <Grid item>
              <FormControlLabel
                control={
                  <IgCheckbox
                    size={"small"}
                    checked={isTestProductsDefaultChange}
                    onChange={(event) => {
                      setIsTestProductsDefaultChange(event.target.checked);
                    }}
                  />
                }
                label={
                  <IgTypography variant={"body2"}>Set as Default</IgTypography>
                }
              />
            </Grid>
          )}
      </StyledGrid>
    );
  }
  return null;
};
